import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './Gallery.css';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.35 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const galleryCategories = ['All', 'Bouquets', 'Weddings', 'Farm', 'Events'];

const galleryItems = [
  { id: 1, src: '/ScarletPetalSymphony.png', category: 'Bouquets', title: 'Scarlet Petal Symphony' },
  { id: 2, src: '/Blush&BerryBloom.png', category: 'Bouquets', title: 'Blush & Berry Bloom' },
  { id: 3, src: '/Wildflower_Sunrise.png', category: 'Bouquets', title: 'Wildflower Sunrise' },
  { id: 4, src: '/coryowner.png', category: 'Weddings', title: 'Cory at the floral arch' },
  { id: 5, src: '/background.png', category: 'Farm', title: 'Greenhouse rows' },
  { id: 6, src: '/DSC_3838.JPG', category: 'Farm', title: 'Rainbow ranunculus field' },
  { id: 7, src: '/DSC_3821.JPG', category: 'Farm', title: 'Pink ombré ranunculus' },
  { id: 8, src: '/DSC_3822.JPG', category: 'Farm', title: 'Butterfly ranunculus' },
  { id: 9, src: '/DSC_3828.JPG', category: 'Farm', title: 'Coral ranunculus' },
  { id: 10, src: '/DSC_3829.JPG', category: 'Farm', title: 'Peach ranunculus cluster' },
  { id: 11, src: '/DSC_3833.JPG', category: 'Farm', title: 'Orange ranunculus' },
  { id: 12, src: '/DSC_3835.JPG', category: 'Farm', title: 'Blush pink ranunculus' },
  { id: 13, src: '/DSC_3858.JPG', category: 'Events', title: 'Lavender & gold bud vase' },
  { id: 14, src: '/DSC_3860.JPG', category: 'Weddings', title: 'Moss runner garden party' },
  { id: 15, src: '/DSC_3865.JPG', category: 'Weddings', title: 'Purple & citrus table bloom' },
  { id: 16, src: '/DSC_3868.JPG', category: 'Events', title: 'Citrus kiss bud vase' },
  { id: 17, src: '/DSC_3878.JPG', category: 'Weddings', title: 'Berry fizz centerpiece' },
  { id: 18, src: '/DSC_3888.JPG', category: 'Weddings', title: 'Sunset ceremony border' },
  { id: 19, src: '/DSC_3892.JPG', category: 'Weddings', title: 'Summer jubilee arrangement' },
  { id: 20, src: '/weddingpic1.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 21, src: '/weddingpic2.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 22, src: '/weddingpic3.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 23, src: '/weddingpic4.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 24, src: '/weddingpic5.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 25, src: '/weddingpic6.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 26, src: '/weddingpic7.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 27, src: '/weddingpic8.webp', category: 'Weddings', title: 'Wedding florals' },
  { id: 28, src: '/weddingpic9.webp', category: 'Weddings', title: 'Wedding florals' },
];

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  const filtered = activeCategory === 'All'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeCategory);

  return (
    <motion.main
      className="gallery"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header__bg">
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&h=600&fit=crop"
            alt="Gallery"
          />
          <div className="page-header__overlay" />
        </div>
        <div className="page-header__content">
          <span className="script-font page-header__eyebrow">Our Work</span>
          <h1>Gallery</h1>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="gallery__content">
        <div className="container">
          <AnimatedSection className="gallery__filters">
            {galleryCategories.map(cat => (
              <button
                key={cat}
                type="button"
                className={`gallery__filter-btn ${activeCategory === cat ? 'gallery__filter-btn--active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </AnimatedSection>

          <div className="gallery__masonry" key={activeCategory}>
            {filtered.map((item) => (
              <div
                key={item.id}
                className="gallery__item"
                onClick={() => setSelectedImage(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedImage(item)}
              >
                <img src={item.src} alt={item.title} loading="lazy" />
                <div className="gallery__item-overlay">
                  <span className="gallery__item-category">{item.category}</span>
                  <h3>{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="lightbox__content"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'tween', duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
              onClick={e => e.stopPropagation()}
            >
              <img src={selectedImage.src} alt={selectedImage.title} />
              <div className="lightbox__info">
                <span className="lightbox__category">{selectedImage.category}</span>
                <h3>{selectedImage.title}</h3>
              </div>
              <button
                className="lightbox__close"
                onClick={() => setSelectedImage(null)}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.main>
  );
};

export default Gallery;
